import { useState, type DragEvent } from 'react'
import { Button, Dropdown, Label } from '@heroui/react'
import type { StaffKey } from '../../lib/staffText'
import { Icon, type IconName } from '../../ui/icons'
import { useStaffText } from '../../ui/StaffText'
import { Find } from '../kit'
import { Funnel } from './Funnel'
import { addField, DATE_GROUPS, filterCount, moveField, movesFor, removeField, usedFields, type Move, type Zone } from './layout'
import type { Dimension, Measure, PivotFields, PivotSpec } from './types'

const ZONES: { zone: Zone; title: StaffKey; hint: StaffKey }[] = [
  { zone: 'rows', title: 'pivot.rows', hint: 'pivot.dropDimension' },
  { zone: 'cols', title: 'pivot.columns', hint: 'pivot.dropDimension' },
  { zone: 'vals', title: 'pivot.values', hint: 'pivot.dropMeasure' },
]
const DRAG_TYPE = 'text/plain'
const MOVES: Record<Move, { key: StaffKey; icon: IconName }> = {
  rows: { key: 'pivot.moveRows', icon: 'swap' },
  cols: { key: 'pivot.moveCols', icon: 'swap' },
  up: { key: 'pivot.moveUp', icon: 'up' },
  down: { key: 'pivot.moveDown', icon: 'down' },
}
const REMOVE = 'remove'

export interface RailProps {
  fields: PivotFields
  layout: PivotSpec
  onChange: (layout: PivotSpec) => void
  /** Reads the values a dimension has, for its funnel. */
  values: (field: string, signal: AbortSignal) => Promise<string[]>
  onRefused: (key: StaffKey) => void
  readOnly?: boolean
}

/** The left rail: Rows / Columns / Values and the Available fields; drag and drop, double-click, the + button or a chip's menu. */
export function Rail({ fields, layout, onChange, values, onRefused, readOnly = false }: RailProps) {
  const { say } = useStaffText()
  const [over, setOver] = useState<Zone | null>(null)
  const [dragging, setDragging] = useState<string | null>(null)
  const measures = new Set(fields.measures.map((m) => m.key))
  const isMeasure = (key: string) => measures.has(key)
  const dimLabel = (key: string) => fields.dimensions.find((d) => d.key === key)?.label ?? key
  const label = (key: string) => (isMeasure(key) ? (fields.measures.find((m) => m.key === key)?.label ?? key) : dimLabel(key))
  const used = usedFields(layout)

  const place = (key: string, zone: Zone, at?: number | null) => {
    if (readOnly) return
    const result = addField(layout, key, zone, isMeasure(key), at)
    if (result.layout) onChange(result.layout)
    else onRefused(result.error)
  }
  const quickAdd = (key: string) => place(key, isMeasure(key) ? 'vals' : 'rows')
  const move = (key: string, to: Move) => {
    const result = moveField(layout, key, to, isMeasure(key))
    if (result.layout) onChange(result.layout)
    else onRefused(result.error)
  }

  const onDragStart = (key: string) => (event: DragEvent) => {
    event.dataTransfer.setData(DRAG_TYPE, key)
    event.dataTransfer.effectAllowed = 'move'
    setDragging(key)
  }
  const onDragEnd = () => {
    setDragging(null)
    setOver(null)
  }
  const onDrop = (zone: Zone, at?: number) => (event: DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    const key = event.dataTransfer.getData(DRAG_TYPE)
    setOver(null)
    setDragging(null)
    if (key) place(key, zone, at)
  }

  const groupWord = DATE_GROUPS.find((g) => g.value === layout.dateGroup)
  return (
    <aside className="rp-rail" aria-label={say('pivot.layout')}>
      {ZONES.map(({ zone, title, hint }) => {
        const keys = layout[zone]
        if (readOnly && keys.length === 0) return null
        return (
          <div key={zone}>
            <div className="rp-zt">
              <span>{say(title)}</span>
              <span className="rp-zc">{keys.length}</span>
            </div>
            <div
              className="rp-zone"
              data-empty={keys.length ? undefined : ''}
              data-readonly={readOnly ? '' : undefined}
              data-over={over === zone ? '' : undefined}
              data-zone={zone}
              onDragOver={(e) => {
                if (readOnly) return
                e.preventDefault()
                setOver(zone)
              }}
              onDragLeave={() => setOver((z) => (z === zone ? null : z))}
              onDrop={onDrop(zone)}
              role="list"
              aria-label={say(title)}
            >
              {keys.length === 0 && <span className="rp-hint">{say(hint)}</span>}
              {keys.map((key, i) => {
                const m = zone === 'vals'
                const n = m ? 0 : filterCount(layout, key)
                const text = label(key) + (key === 'date' && groupWord ? ` · ${say(groupWord.key)}` : '')
                return (
                  <div
                    key={key}
                    role="listitem"
                    aria-label={readOnly ? text : say('pivot.placedChip', { field: text, zone: say(title) })}
                    className="rp-chip"
                    data-measure={m ? '' : undefined}
                    data-placed=""
                    data-dragging={dragging === key ? '' : undefined}
                    draggable={!readOnly}
                    onDragStart={onDragStart(key)}
                    onDragEnd={onDragEnd}
                    onDrop={onDrop(zone, i)}
                  >
                    {!readOnly && (
                      <span className="rp-grip">
                        <Icon name="drag" size={12} />
                      </span>
                    )}
                    <span className="rp-chip__label">{text}</span>
                    {!m && (!readOnly || n > 0) && <Funnel field={key} label={dimLabel(key)} layout={layout} onChange={onChange} values={values} count={n} readOnly={readOnly} />}
                    {!readOnly && (
                      <Dropdown>
                        <Button isIconOnly size="sm" variant="ghost" className="rp-ic" aria-label={say('pivot.move', { field: label(key) })}>
                          <Icon name="more" size={14} />
                        </Button>
                        <Dropdown.Popover placement="bottom end">
                          <Dropdown.Menu
                            aria-label={say('pivot.move', { field: label(key) })}
                            onAction={(id) => (id === REMOVE ? onChange(removeField(layout, key)) : move(key, id as Move))}
                          >
                            {movesFor(layout, key).map((to) => (
                              <Dropdown.Item key={to} id={to} textValue={say(MOVES[to].key)}>
                                <Icon name={MOVES[to].icon} size={16} />
                                <Label>{say(MOVES[to].key)}</Label>
                              </Dropdown.Item>
                            ))}
                            <Dropdown.Item id={REMOVE} textValue={say('pivot.removeShort')} variant="danger">
                              <Icon name="close" size={16} />
                              <Label>{say('pivot.removeShort')}</Label>
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown.Popover>
                      </Dropdown>
                    )}
                    {!readOnly && (
                      <Button isIconOnly size="sm" variant="ghost" className="rp-ic" aria-label={say('pivot.remove', { field: label(key) })} onPress={() => onChange(removeField(layout, key))}>
                        <Icon name="close" size={13} />
                      </Button>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}
      {!readOnly && (
        <div className="rp-avail">
          <div className="rp-zt rp-zt--available">
            <span>{say('pivot.available')}</span>
          </div>
          <Available fields={fields} used={used} onDragStart={onDragStart} onDragEnd={onDragEnd} onAdd={quickAdd} dragging={dragging} />
        </div>
      )}
    </aside>
  )
}

function Available({
  fields,
  used,
  onDragStart,
  onDragEnd,
  onAdd,
  dragging,
}: {
  fields: PivotFields
  used: Set<string>
  onDragStart: (key: string) => (event: DragEvent) => void
  onDragEnd: () => void
  onAdd: (key: string) => void
  dragging: string | null
}) {
  const { say } = useStaffText()
  const [find, setFind] = useState('')
  const [shut, setShut] = useState<Set<string>>(() => new Set())
  const words = find.trim().toLocaleLowerCase()
  const fits = (item: Dimension | Measure) => !words || item.label.toLocaleLowerCase().includes(words)
  const groups: [string, (Dimension | Measure)[], boolean][] = []
  for (const d of fields.dimensions) {
    if (used.has(d.key) || !fits(d)) continue
    const group = groups.find(([g]) => g === d.group)
    if (group) group[1].push(d)
    else groups.push([d.group, [d], false])
  }
  const measures = fields.measures.filter((m) => !used.has(m.key) && fits(m))
  if (measures.length) groups.push([say('pivot.measures'), measures, true])
  const toggle = (group: string) =>
    setShut((now) => {
      const next = new Set(now)
      if (!next.delete(group)) next.add(group)
      return next
    })
  const chip = (item: Dimension | Measure, measure: boolean) => (
    <div
      key={item.key}
      className="rp-chip"
      data-measure={measure ? '' : undefined}
      data-dragging={dragging === item.key ? '' : undefined}
      draggable
      title={say('pivot.dragHint')}
      onDragStart={onDragStart(item.key)}
      onDragEnd={onDragEnd}
      onDoubleClick={() => onAdd(item.key)}
    >
      <span className="rp-grip">
        <Icon name="drag" size={12} />
      </span>
      <span className="rp-chip__label">{item.label}</span>
      <Button isIconOnly size="sm" variant="ghost" className="rp-ic rp-add" aria-label={say('pivot.add', { field: item.label })} onPress={() => onAdd(item.key)}>
        <Icon name="plus" size={13} />
      </Button>
    </div>
  )
  return (
    <div className="rp-available">
      <Find fullWidth className="rp-find" label={say('pivot.findField')} value={find} onChange={setFind} />
      {groups.length === 0 && <p className="rp-hint">{say('pivot.noField')}</p>}
      {groups.map(([group, items, measure]) => {
        // A search shows every match, whatever was collapsed.
        const open = !!words || !shut.has(group)
        return (
          <div key={group} className="rp-available__group">
            <Button variant="ghost" size="sm" className="rp-g" aria-expanded={open} onPress={() => toggle(group)}>
              <Icon name="chevron" size={12} />
              {group}
              <span className="rp-zc">{items.length}</span>
            </Button>
            {open && <div className="rp-av">{items.map((item) => chip(item, measure))}</div>}
          </div>
        )
      })}
    </div>
  )
}
